import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import SearchableSelect from '../ui/SearchableSelect';
import { listTree } from '../../extensions/bundled/documents/documentsStore';
import { listScopes } from '../../extensions/bundled/documents/documentsScopes';
import { moveTargets, moveToTarget } from '../../extensions/bundled/documents/documentsActions';
import { bumpDocumentsTree } from '../../extensions/bundled/documents/documentsUiStore';
import { relocateDocumentTabs } from './relocateTabs';
import { documentsScopesEnabled } from '../../extensions/bundled/documents/documentsFlags';
import { useNotificationStore } from '../../store/notificationStore';
import { errorMessage } from '../../utils/errorMessage';

const keyOf = (target) => `${target.kind}:${target.id ?? target.scopeId ?? ''}`;

/**
 * "Move to…" for the open document (cards 3.1 / 3.4): a SearchableSelect of
 * every place the page may go — the top of its tree, a folder, another page
 * (as a sub-page), or another personal workspace — from `moveTargets`, run by
 * `moveToTarget`. A move into another workspace re-scopes the open tab.
 */
export default function MoveToModal({ documentId, scope, onClose }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ nodes: null, scopes: [] });
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Other workspaces are targets only while workspaces are on (card 5.5).
    const personal = (!scope?.type || scope.type === 'personal') && documentsScopesEnabled();
    Promise.all([listTree(scope), personal ? listScopes().catch(() => []) : Promise.resolve([])])
      .then(([nodes, scopes]) => {
        if (!cancelled) setState({ nodes, scopes: scopes || [] });
      })
      .catch(() => {
        if (!cancelled) setState({ nodes: [], scopes: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const node = useMemo(() => (state.nodes || []).find((n) => String(n.id) === String(documentId)) || null, [state.nodes, documentId]);
  const options = useMemo(() => {
    if (!node) return [];
    return moveTargets(node, state.nodes, { scope, scopes: state.scopes, pages: true }).map((target) => {
      let label;
      if (target.kind === 'root') label = t('docEditor.move.root');
      else if (target.kind === 'workspace') label = t('docEditor.move.workspace', { name: target.name || t('docEditor.scope.personal') });
      else label = (target.path || []).map((p) => p || t('docEditor.untitled')).join(' / ');
      return { value: keyOf(target), label, target };
    });
  }, [node, state, scope, t]);

  const move = async () => {
    const picked = options.find((o) => o.value === choice);
    if (!picked || !node) return;
    setBusy(true);
    try {
      await moveToTarget(node, picked.target, scope, state.nodes);
      bumpDocumentsTree();
      // The page and its sub-pages: every open tab follows its document.
      if (picked.target.kind === 'workspace') await relocateDocumentTabs();
      useNotificationStore.getState().success(t('docEditor.move.done', { place: picked.label }), t('docEditor.move.doneTitle'));
      onClose();
    } catch (err) {
      const code = err?.code ? `docStore.errors.${err.code}` : null;
      useNotificationStore
        .getState()
        .error(code ? t(code) : errorMessage(err, t('docEditor.move.failed')), t('docEditor.actions.failedTitle'));
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={t('docEditor.move.title')}>
      <div className="space-y-4">
        <SearchableSelect
          options={options}
          value={choice}
          onChange={(key) => setChoice(key)}
          placeholder={t('docEditor.move.placeholder')}
          searchPlaceholder={t('docEditor.move.search')}
          loading={state.nodes == null}
          emptyText={t('docEditor.move.empty')}
          noMatchText={t('docEditor.move.noMatch')}
        />
        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!choice || busy}
            onClick={move}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {t('docEditor.move.submit')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
